import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EntitlementsHelperService } from '@ptl/account-management';
import { HTTPWrapperContext, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES, AxwayTokenFetcherService } from '@ptl/xhr';
import { BehaviorSubject, Observable } from 'rxjs';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { ProducerStateStructure } from '../../configs/interfaces/producerStateStructure.interface';
import { ResourcesListStateStructure } from '../configs/reducers/interfaces/resources-list.state.interface';
import { RESOURCES_LIST_REDUCER } from '../configs/reducers/resources-list.reducer';
import { ResourcesListDAMResponse, ResourcesListItem } from './interfaces/ResourcesListDAM.response.interface';
import { CommonUtilityService } from 'src/app/shared/utility-services/common-utility.service';
import { API_ENDPOINTS } from 'src/app/shared/constants/api-endpoints.constant';

@Injectable({
  providedIn: 'root'
})
export class ResourcesListService {

  uploadSuccessSubject: BehaviorSubject<ResourcesListItem> = new BehaviorSubject<ResourcesListItem>(null);
  uploadSuccessSubjectObservable$: Observable<ResourcesListItem> = this.uploadSuccessSubject.asObservable();

  constructor(
    private httpClient: HTTPWrapperContext,
    private reducerUtilityService: ReducerUtilityService,
    private entitlementsService: EntitlementsHelperService,
    private commonUtility: CommonUtilityService,
    private axwayTokenFetcherService: AxwayTokenFetcherService
  ) { }

  /**
   * 
   * @param servicePath servlet API path
   * @param docRoot asset folder path
   * @param types type tags
   * @param topics topic tags
   * @param sortingOptions ascending or descending values
   * @param timeframe in days
   * @param searchText search query text
   * @param start index to start from
   * @param limit # of items to fetch
   * @param regions region tags for filtering (e.g., ['ALL', 'WPA'])
   * @returns 
   */
  async getDamData(
    servicePath: string, 
    docRoot: string, 
    types: string[] = [], 
    topics: string[] = [], 
    sortingOptions: Map<string, 'asc' | 'desc'>,
    timeframe?: number, 
    searchText?: string,
    start: number = 0, 
    limit: number = 25,
    regions: string[] = []
  ): Promise<ResourcesListDAMResponse> {

    // console.log('servicePath', servicePath);
    // console.log('docRoot', docRoot);

    const state: ResourcesListStateStructure = this.reducerUtilityService.getState(RESOURCES_LIST_REDUCER);

    /**
     * https://medicaredenv2.highmark.com
     * /content/highmark/en/highmarkbcbs/shopx/resources/individual.assetsWithTags.json
     * ?docRoot=/content/dam/highmark/en/healthco/individualdocs
     * &queryType=textsearch
     * &start=0
     * &limit=3
     */

    let queryString = '';

    /** Market/Category Query setup */
    let catString = '';
    state.filters.resourceCategories.forEach((item, index) => {
      if (item.value.checked) {
        catString = catString.concat(`${item.key}`).concat(',');
      }
    });

    /** Add if not empty */
    if (catString.length) {
      queryString = queryString.concat(catString);
    }

    /** Type and Topics */
    if (types.length) {
      let catString = '';
      types.forEach( t => {
        catString = catString.concat(t).concat(',');
      })
      /** Add if not empty */
      if (catString.length) {
        queryString = queryString.concat(catString);
      }
    }
    if (topics.length) {
      let catString = '';
      topics.forEach( t => {
        catString = catString.concat(t).concat(',');
      })
      /** Add if not empty */
      if (catString.length) {
        queryString = queryString.concat(catString);
      }
    }

    /** Regions */
    if (regions.length) {
      let regionString = '';
      regions.forEach( r => {
        regionString = regionString.concat(r).concat(',');
      })
      /** Add if not empty */
      if (regionString.length) {
        queryString = queryString.concat(regionString);
      }
    }

    let orderAttr = null; // Filter to sort on
    let orderDirection = null; // Sort direction
    if (sortingOptions) {
      let sortIterator = sortingOptions.keys()
      let item = sortIterator.next();
      while (!item.done) {
        // do stuff
        const key = item.value;
        const value = sortingOptions.get(key);
        orderAttr = key;
        orderDirection = value;
        item = sortIterator.next();
      }
    }
    
    if (queryString.length > 0) {
      const requestConfigImplementation = new RequestConfigImplementation({
        method: REQUEST_METHODS.GET,
        url: servicePath,
        GET_PAYLOAD: {
          docRoot: docRoot, // '/content/dam/highmark/en/healthco/individualdocs',
          queryType: 'textsearch',
          query: queryString,
          start: start,
          limit: limit.toString()
        }
      });

      /** Add dynamic SORT predicate */
      if ( orderAttr && orderDirection ) {
        requestConfigImplementation.GET_PAYLOAD['orderByType'] = orderDirection;
        requestConfigImplementation.GET_PAYLOAD['orderBy'] = orderAttr;
      }

      /** Add dynamic TIMEFRAME predicate */
      if ( timeframe ) {
        requestConfigImplementation.GET_PAYLOAD['timeframe'] = timeframe;
      }

      /** Add dynamic SEARCH TEXT predicate */
      if ( searchText && searchText.trim().length > 0 ) {
        requestConfigImplementation.GET_PAYLOAD['searchText'] = searchText.trim();
      }

      /** This should probably be 'datapower' strategy when not in local - or - Apache needs to be better config'd */
      this.httpClient.setStrategy(XHR_STRATEGIES.MOCK);
      return this.httpClient.makeRequest<ResourcesListDAMResponse>(requestConfigImplementation)
        .then((response: ResourcesListDAMResponse) => {
          response.output.forEach(item => {
            // This might not be needed
            /** Provide default value for tagless items (allows the dropdown to have a value) */
            if (!item.type) { item.type = ['(Empty)']; }
            if (!item.topic) { item.topic = ['(Empty)']; }
          });
          return response;
        })
        .catch(failure => failure)
        .finally(() => null);
    } else {
      return Promise.resolve(null);
    }
  }

  async sendDocumentInEmail(
    isMember: boolean,
    deliveryRecipient: string,
    etAttributes: { [key: string]: string }[]
  ): Promise<any> {

    const emailTemplates = {
      WPA: 'ProducerSharedHMBCBS',
      CPA: 'ProducerSharedPBS',
      WV: 'ProducerSharedWV',
      DE: 'ProducerSharedDE',
      NEPA: 'ProducerSharedHMBCBS',
      MINCR: 'ProducerSharedMINCR',
      ND: 'ProducerSharedND',
      WY: 'ProducerSharedWY'
    };

    const selectedAgency = await this.entitlementsService.getSelectedAgencyPromise();

    const payload = JSON.stringify({
      baseMessage: { headerVersion: '1.0' },
      payload: {
        member: isMember,
        deliveryRecipient: deliveryRecipient,
        deliveryMethod: 'EMAIL',
        templateName: emailTemplates[selectedAgency.region] || 'ProducerSharedHMBCBS',
        etAttributes: etAttributes
      }
    });

    const env = this.commonUtility.getTestEnvironment();
    const requestConfigImplementation = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      headers: new HttpHeaders({
        'x-hmhs-test-backend': `wasinter${env}.highmark.com`
      }),
      POST_OR_PUT_PAYLOAD: payload,
      url: this.axwayTokenFetcherService.buildApiUrl(API_ENDPOINTS.DOCUMENT_INFO.NAS_SBC_DOC_SEARCH, {}),
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS_V4);
    return this.httpClient.makeRequest<any>(requestConfigImplementation).catch(err => err);
  }
}
